/**
 * Navigation component overrides: tabs, breadcrumbs, links, menus, lists, steppers.
 * @see https://mui.com/material-ui/components/tabs/
 * @see https://mui.com/material-ui/components/breadcrumbs/
 * @see https://mui.com/material-ui/components/links/
 * @see https://mui.com/material-ui/components/menus/
 * @see https://mui.com/material-ui/components/lists/
 * @see https://mui.com/material-ui/components/steppers/
 */
import varnishTokens from '@allenai/varnish-theme/mui';
import type { Components, Theme } from '@mui/material';

export const navigationComponents: Components<Omit<Theme, 'components'>> = {
    MuiBreadcrumbs: {
        styleOverrides: {
            root: { fontSize: varnishTokens.font.size.sm.value },
        },
    },
    MuiLink: {
        defaultProps: {
            underline: 'none',
        },
        styleOverrides: {
            root: { fontWeight: varnishTokens.font.weight.medium.value },
        },
        variants: [
            {
                props: { color: 'default' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.links.default,
                        '&:hover': { color: palette.links.hovered.default },
                    };
                },
            },
            {
                props: { color: 'reversed' },
                style: ({ theme }) => {
                    const { palette } = theme.vars ?? theme;
                    return {
                        color: palette.links.reversed,
                        '&:hover': { color: palette.links.hovered.reversed },
                    };
                },
            },
            {
                props: { color: 'text' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.default.main }),
            },
            {
                props: { color: 'primary' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.primary.main }),
            },
            {
                props: { color: 'secondary' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.secondary.main }),
            },
            {
                props: { color: 'tertiary' },
                style: ({ theme }) => ({ color: (theme.vars ?? theme).palette.tertiary.main }),
            },
            {
                props: { size: 'small' },
                style: { fontSize: varnishTokens.font.size.sm.value },
            },
            {
                props: { size: 'medium' },
                style: { fontSize: varnishTokens.font.size.md.value },
            },
            {
                props: { size: 'large' },
                style: { fontSize: varnishTokens.font.size.lg.value },
            },
        ],
    },
    MuiListItemButton: {
        styleOverrides: {
            root: {
                fontSize: varnishTokens.font.size.sm.value,
            },
        },
    },
    MuiListSubheader: {
        styleOverrides: {
            root: { fontSize: varnishTokens.font.size.sm.value },
        },
    },
    MuiMenu: {
        styleOverrides: {
            paper: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    backgroundColor: palette.elements.overlay.background,
                    border: '1px solid',
                    borderColor: palette.divider,
                    padding: theme.spacing(0.5),
                };
            },
            list: { padding: 0 },
        },
    },
    MuiMenuList: {
        styleOverrides: {
            root: ({ theme }) => ({
                padding: theme.spacing(0.5),
            }),
        },
    },
    MuiMenuItem: {
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    '&:hover': { backgroundColor: palette.action.hover },
                    '&.Mui-selected': {
                        backgroundColor: palette.action.selected,
                        '&:hover': { backgroundColor: palette.action.selected },
                    },
                    '&.Mui-disabled': { color: palette.text.disabled },
                };
            },
        },
    },
    MuiStepLabel: {
        styleOverrides: {
            label: { fontSize: varnishTokens.font.size.sm.value },
        },
    },
    MuiTab: {
        styleOverrides: {
            root: ({ theme }) => {
                const { palette } = theme.vars ?? theme;
                return {
                    textTransform: 'none',
                    padding: '8px 16px',
                    fontSize: varnishTokens.font.size.sm.value,
                    minHeight: 'unset',
                    color: palette.text.primary,
                    '&:hover:not(.Mui-disabled)': { color: palette.primary.main },
                    '&.Mui-selected:not(.Mui-disabled)': { color: palette.primary.main },
                    '&.Mui-disabled': { color: palette.text.disabled },
                };
            },
        },
    },
    MuiTabs: {
        styleOverrides: {
            flexContainer: { gap: varnishTokens.spacing[1].value },
        },
    },
};
